import React, { Suspense, useEffect, useMemo, lazy, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from "@litres/ui-kit/popover";
import { ModalOverlay } from "@litres/ui-kit/modal";
import { api, useAppSelector } from "@litres-core/store";
import {
  checkIfRussianLanguage,
  deleteCookie,
  getCookie,
  setCookie,
} from "@litres/utils";
import { useTranslation } from "@litres-libs/i18n";
import type { RootState, AppDispatch } from "@litres-core/store";
import { SkeletonBlock } from "@litres/ui-kit/skeleton";
import { useHasWindow } from "@litres-libs/global-hooks";
import { useAbTest } from "@litres-libs/experiments";
import type { IUserProps } from "./User.types";
import Login from "./Login/Login";
import Profile from "./Profile/Profile";
import { EPopups } from "../../utils/enums";
import { IS_PHONE_ASKED_COOKIE, ONE_WEEK_IN_SEC } from "../../constants";
import { HeaderContext } from "../../Header";

const AuthPopup = lazy(() => import("@litres/auth/popup"));
const AuthorizationPopup = lazy(
  () => import("@litres-modules/authorization-popup")
);

import styles from "./User.module.scss";

export const User = (props: IUserProps): JSX.Element => {
  const { isAnon, loginPopupError, openedPopup, setOpenedPopup, isLibrary } =
    props;

  const { t } = useTranslation();
  const {
    id,
    profile,
    library_info: libraryInfo,
    socials,
  } = useSelector((state: RootState) => state.foundationUser);
  const { isSchoolDomain, isForeignDomain, isLibraryDomain } =
    useContext(HeaderContext);
  const hasWindow = useHasWindow();
  const isAbTest1128 = useAbTest(1128);
  const popupAfterAuth = useMemo(
    () => hasWindow && getCookie("popup_after_auth"),
    [hasWindow]
  );
  const popupAsking = useMemo(
    () => hasWindow && getCookie("popup_asking"),
    [hasWindow]
  );

  /* заполнена хотя бы одна соцсеть */
  const attachedSocnet = socials[0] || undefined;
  /* заполнено недостаточное количество контактов из множества [телефон, эмейл, соцсеть] (нужно 2 и более) */
  const hasTooManyEmptyFields =
    [profile.phone_number, profile.email, attachedSocnet].filter(Boolean)
      .length <= 1;
  /* пользователь является библиоюзером */
  const isOneOfLibraryUser =
    libraryInfo.is_library_user ||
    libraryInfo.is_school_domain ||
    libraryInfo.is_librarian;
  /* показать модалку дозаполнения данных */
  const isShowAskingModal =
    id !== 0 &&
    hasTooManyEmptyFields &&
    !isOneOfLibraryUser &&
    Boolean(popupAsking);
  /* показать модалку приветствия */
  const isShowWelcomeModal = id !== 0 && Boolean(popupAfterAuth);
  const isAsking = isShowAskingModal || isShowWelcomeModal;

  const { isAuthPopupVisible: isPopupOpen } = useAppSelector(
    state => state.global.header.auth
  );

  const isOpenLoginPopupError = loginPopupError?.isOpen;
  const loginPopupErrorUIOptions = loginPopupError?.uiOptions;

  const dispatch = useDispatch<AppDispatch>();
  /**
   * Не показываем новую форму для школьников и библиопользователей
   * Пока не выкатим ACQN-264
   * TODO: убрать после раскатки ACQN-264
   */
  const isSchoolOrBiblioDomain = isLibraryDomain || isSchoolDomain;

  const lang = useSelector<RootState, string>(
    state => state.face.language || "ru"
  );
  const shouldShowNewAuth = hasWindow
    ? /* ACQN-233: в личном кабинете можем поднимать popup для подтверждения телефона 
    TODO: текущее состояние auth-popup не позволяет грамотно управлять подсостояниями
    */
      window.location.pathname === "/pages/personal_cabinet_login/" ||
      window.location.pathname === "/pages/my_settings/" ||
      Boolean(getCookie("new_auth")) ||
      Boolean(window.location.search.includes("new_auth=1")) ||
      isAbTest1128 ||
      checkIfRussianLanguage(lang) ||
      isForeignDomain
    : false;

  /* модалка может быть открыта (автоматически или кликом) */
  const isShowAuthPopup = isAnon || (!isAnon && isAsking);

  /**
   * @deprecated
   * NOTE: блокирует вызов открытия попапа авторизации/регистрации
   * Убираем и смотрим, в теории ничего не должно сломать
   */
  useEffect(() => {
    // if (
    //   isPopupOpen &&
    //   openedPopup !== EPopups.User &&
    //   openedPopup !== EPopups.Default &&
    //   /*
    //    * NOTE: Из-за условий выше не можем открыть попап авторизации на страницах /me/*
    //    * Таким образом оставляем логику этих условий везде кроме страниц /me/*
    //    */
    //   !checkIfCurrentPageIsUserProfile(window.location.pathname)
    // ) {
    //   hidePopup();
    // }
  }, [openedPopup, isPopupOpen]);

  useEffect(() => {
    if (isOpenLoginPopupError) {
      onVisibleChange(true);
    }
  }, [isOpenLoginPopupError]);

  const resetStoreInterfaceRequests = (): void => {
    dispatch(
      api.global.interfaceRequests.setLoginPopupError({ isOpen: false })
    );
  };

  const hidePopup = (): void => {
    if (!isAnon && shouldShowNewAuth) {
      // для зарегистрированного пользователя при закрытии окна Привязать телефон, ставим куку на неделю
      // когда кука сгорит спрашиваем телефон у пользователя повторно.
      // только для новой авторизации
      setCookie(IS_PHONE_ASKED_COOKIE, "1", { "max-age": ONE_WEEK_IN_SEC });
    }
    dispatch(api.global.auth.setRateLimitError(false));
    onVisibleChange(false);
  };

  const onVisibleChange = (visibility: boolean): void => {
    dispatch(api.global.header.auth.setAuthPopupVisibility(visibility));

    if (visibility) {
      setOpenedPopup(EPopups.User);
    } else {
      if (isPopupOpen) {
        if (isOpenLoginPopupError) {
          resetStoreInterfaceRequests();
        }
        if (isShowAskingModal) {
          deleteCookie("popup_asking");
        }
      }
    }
  };

  return (
    <div className={styles.wrapper} data-testid="user-button">
      {shouldShowNewAuth ? (
        <>
          {isAnon ? (
            <div
              onClick={() => {
                onVisibleChange(true);
              }}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  onVisibleChange(true);
                }
              }}
            >
              <Login authorizationPopupVisibility={isPopupOpen} />
            </div>
          ) : (
            <Profile />
          )}
          <ModalOverlay
            isOpen={isPopupOpen}
            closeOnClickOutside={
              !loginPopupErrorUIOptions?.noCancel &&
              !loginPopupErrorUIOptions?.fullScreen
            }
            onClose={hidePopup}
            isFullScreenMode={loginPopupErrorUIOptions?.fullScreen}
            isCloseButtonShown={!loginPopupErrorUIOptions?.noCancel}
          >
            <Suspense fallback={<SkeletonBlock height="100%" width="100%" />}>
              <AuthPopup
                hidePopup={hidePopup}
                errorTitle={t(loginPopupError?.errorText ?? "")}
                isLibrary={isSchoolOrBiblioDomain}
                uiOptions={loginPopupErrorUIOptions}
              />
            </Suspense>
          </ModalOverlay>
        </>
      ) : (
        <Popover
          placement="bottom-end"
          disableArrow
          strategy="fixed"
          trigger="click"
          baseClassName={styles.loginPopupParent}
          visible={isPopupOpen}
          onVisibleChange={onVisibleChange}
          offset={[20, 0]}
          portal={false}
          content={
            isShowAuthPopup ? (
              <Suspense fallback={<SkeletonBlock height="100%" width="100%" />}>
                <AuthorizationPopup
                  hidePopup={hidePopup}
                  errorTitle={
                    loginPopupError?.errorText && t(loginPopupError.errorText)
                  }
                  isLibrary={isLibrary}
                  showSocials={!isLibrary}
                  showPhoneButton={!isLibrary}
                  showAppLoginButton={!isLibrary}
                  showLibraryTicketButton={!isLibrary}
                  showGDPRAgreement={!isLibrary}
                  customTitle={
                    isLibrary ? "Вход для читателей и библиотекарей" : ""
                  }
                />
              </Suspense>
            ) : null
          }
          contentClassName={styles.loginPopup}
          customCloseTriggers={["esc"]}
          closeOnClick={false}
          hasMask={isShowAuthPopup}
        >
          {isAnon ? (
            <Login authorizationPopupVisibility={isPopupOpen} />
          ) : (
            <Profile />
          )}
        </Popover>
      )}
    </div>
  );
};
